/**
 * Detect block vs inline markdown code and language (Apex heuristics, CSV heuristics).
 */
import type { ReactNode } from "react";

/** Regex to extract class/trigger/interface/enum name from Apex code */
const APEX_NAME_RE =
  /\b(?:public|private|global)\s+(?:with\s+sharing\s+|without\s+sharing\s+|virtual\s+|abstract\s+)*(?:class|interface|enum)\s+(\w+)/;
const TRIGGER_NAME_RE = /\btrigger\s+(\w+)\s+on\s+/;

export function extractApexName(code: string): string | undefined {
  return (code.match(APEX_NAME_RE) || code.match(TRIGGER_NAME_RE))?.[1];
}

export type LanguageMatch = RegExpExecArray | null;

/**
 * Resolve fenced code string, block vs inline, and language- class match (with Apex/CSV fallbacks).
 */
export function resolveMarkdownCodeBlock(
  className: string | undefined,
  inline: boolean | undefined,
  children: ReactNode,
): { codeString: string; isBlock: boolean; match: LanguageMatch } {
  let match = /language-(\w+)/.exec(className || "");
  const codeString = String(children).replace(/\n$/, "");
  const isBlock =
    inline === false ||
    (inline == null && (!!match || codeString.includes("\n")));

  if (isBlock && !match) {
    const looksLikeApex =
      /\b(public|private|global|class|trigger|static|void|List<|Map<|Set<|@isTest|@AuraEnabled|System\.|Test\.)\b/.test(
        codeString,
      );
    if (looksLikeApex) match = ["language-apex", "apex"] as unknown as RegExpExecArray;
  }

  if (isBlock && !match) {
    const trimmed = codeString.trim();
    const lines = trimmed.split("\n").map((line) => line.trim()).filter(Boolean);
    const joined = lines.slice(0, 12).join("\n");

    if (
      /^export\s+(default\s+)?(function|class|const)\b/m.test(joined) ||
      /^import\s+.+\s+from\s+["'][^"']+["'];?$/m.test(joined) ||
      /^function\s+[A-Za-z_][A-Za-z0-9_]*\s*\(/m.test(joined)
    ) {
      match = ["language-javascript", "javascript"] as unknown as RegExpExecArray;
    } else if (
      /^\.[A-Za-z_-][A-Za-z0-9_-]*\s*\{/m.test(joined) ||
      /^#[A-Za-z_-][A-Za-z0-9_-]*\s*\{/m.test(joined) ||
      /^[A-Za-z][A-Za-z0-9:_-]*(?:\s*,\s*[A-Za-z][A-Za-z0-9:_-]*)*\s*\{/m.test(joined) ||
      /^@media\b/m.test(joined)
    ) {
      match = ["language-css", "css"] as unknown as RegExpExecArray;
    } else if (
      /^def\s+[A-Za-z_][A-Za-z0-9_]*\s*\(/m.test(joined) ||
      /^from\s+[A-Za-z0-9_.]+\s+import\s+/m.test(joined)
    ) {
      match = ["language-python", "python"] as unknown as RegExpExecArray;
    } else if (
      /^#!/.test(trimmed) ||
      /^(echo|export|if\s+\[|fi|for\s+\w+\s+in|curl\s+|npm\s+|pnpm\s+|yarn\s+)/m.test(joined)
    ) {
      match = ["language-bash", "bash"] as unknown as RegExpExecArray;
    } else if (
      /^(public\s+)?class\s+[A-Z][A-Za-z0-9_]*\s*\{/m.test(joined) ||
      /^public\s+(static\s+)?(void|int|String|boolean|long|double)\b/m.test(joined)
    ) {
      match = ["language-java", "java"] as unknown as RegExpExecArray;
    }
  }

  if (isBlock && !match && codeString.includes(",") && codeString.includes("\n")) {
    const lines = codeString.split("\n").filter((l) => l.trim()).slice(0, 3);
    if (lines.length >= 2) {
      const counts = lines.map((l) => (l.match(/,/g) || []).length);
      const allConsistent = counts[0] >= 1 && counts.every((c) => c === counts[0]);
      if (allConsistent) match = ["language-csv", "csv"] as unknown as RegExpExecArray;
    }
  }

  return { codeString, isBlock, match };
}
